import { useTranslation } from "next-i18next";
import { oneOf, shape, string } from "prop-types";
import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Head from "next/head";

import { locales } from "~config/locale";
import classnames from "~utils/classnames/classnames";

import Breadcrumbs from "~components/Breadcrumbs";
import Button from "~components/Button";
import useBreadcrumbs from "~hooks/useBreadcrumbs";
import LinksListGrid from "~modules/LinksListGrid";
import DuoColorText from "~ts_components/DuoColorText/DuoColorText";

import { LinksListGridType } from "~types/index";

import classes from "./ErrorPage.module.scss";

const HeroImage = dynamic(() => import("~public/images/404page.svg"), {
  ssr: false,
});

const getPageTitle = (t, code) => {
  switch (code) {
    case 404:
      return t("errors:notFoundPageTitle");
    default:
      return t("errors:generalPageTitle");
  }
};

const getErrorMessage = (t, code) => {
  switch (code) {
    case 404:
      return (
        <>
          <h1 className={classnames(classes.title, classes.colored)}>
            {t("errors:notFoundPageTitle")}
          </h1>
          <h2 className={classes.subtitle}>
            {t("errors:notFoundPageSubtitle")}
          </h2>
          <p className={classes.body}>{t("errors:notFoundPageText")}</p>
        </>
      );
    default:
      return (
        <>
          <h1 className={classes.title}>
            <DuoColorText
              text={getPageTitle(t, code)}
              emphasis={t("errors:generalPageTitleEmphasis")}
            />
          </h1>
          <p className={classes.body}>
            {t("errors:generalPageText", { code })}
          </p>
        </>
      );
  }
};

// use a wrapper component so we can conditionally render the breadcrumbs
// and use the useBreadcrumbs hook (which fetches the breadcrumbs from the API)
// only when needed
const BreadcrumbsLoader = ({ pageTitle }) => {
  // error page must be statically rendered, so we get the breadcrumbs from the API
  const breadcrumbs = useBreadcrumbs();

  return (
    <div className={classes.breadcrumbs}>
      <Breadcrumbs
        crumbs={[
          ...breadcrumbs,
          {
            title: pageTitle,
            url: "/",
          },
        ]}
      />
    </div>
  );
};

BreadcrumbsLoader.propTypes = {
  pageTitle: string.isRequired,
};

const BackHomeButton = () => {
  const { t } = useTranslation();
  const parentPaths = useBreadcrumbs();
  const parentSlug =
    parentPaths.length > 0 ? parentPaths[parentPaths.length - 1].url : "/";

  return (
    <Button color="black" href={parentSlug} className={classes.cta}>
      {t("errors:notFoundPageCtaLabel")}
    </Button>
  );
};

const ErrorPage = ({ code = 500, currentDomain }) => {
  const { t } = useTranslation();
  const [shouldFetchBreadcrumbs, setShouldFetchBreadcrumbs] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      const urlParts = url.pathname
        .split("/")
        .filter(Boolean)
        .filter((p) => !locales.includes(p));
      if (code === 404 && urlParts.length < 2) {
        // we're on the first level and this level already renders a 404 page
        // so trying to fetch breadcrumbs has no use
        setShouldFetchBreadcrumbs(false);
        return;
      }
      const isFile = urlParts[urlParts.length - 1].includes(".");
      // if the url has an extension, it's a file, so we don't need breadcrumbs
      setShouldFetchBreadcrumbs(!isFile);
    }
  }, [code]);

  return (
    <>
      <Head>
        <title>{`${code} - ${getPageTitle(t, code)}`}</title>
      </Head>
      {shouldFetchBreadcrumbs ? (
        <BreadcrumbsLoader pageTitle={`${code}`} />
      ) : (
        <div className={classes.breadcrumbs}>
          <Breadcrumbs
            crumbs={[
              {
                title: `${code}`,
                url: "/",
              },
            ]}
          />
        </div>
      )}
      <div className={classnames(classes.ErrorPage)}>
        <div className={classes.topContent}>
          <div className={classes.image}>
            <HeroImage />
          </div>
          <div className={classes.content}>
            {getErrorMessage(t, code)}
            <BackHomeButton />
          </div>
        </div>
        {Boolean(currentDomain?.errorSuggestions?.lists?.length) && (
          <div className={classes.bottomContent}>
            <LinksListGrid
              title={currentDomain?.errorSuggestions?.title}
              items={currentDomain?.errorSuggestions?.lists}
            />
          </div>
        )}
      </div>
    </>
  );
};

ErrorPage.propTypes = {
  code: oneOf([404, 500]),
  currentDomain: shape({
    errorSuggestions: shape(LinksListGridType),
  }).isRequired,
};

export default ErrorPage;
